import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Route, Handshake, Users, ListChecks, HeadphonesIcon, Sparkles, ArrowRight, Instagram } from "lucide-react";
import { SectionTitle } from "../components/site/PublicLayout";
import { TourCard } from "../components/site/TourCard";
import { Testimonials } from "../components/site/Testimonials";
import { useFetch, useSettings } from "../lib/hooks";
import { FAMILY_DESTINATIONS, MONTHS, tourPath, durationLabel, imgUrl } from "../lib/site";
import useSeo from "../lib/useSeo";

const HeroSearch = ({ tours }) => {
  const nav = useNavigate();
  const [d, setD] = useState("");
  const [m, setM] = useState("");
  const [n, setN] = useState(2);
  const dests = useMemo(() => [...new Set((tours || []).map((t) => t.destination))], [tours]);
  const go = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (d) p.set("destination", d);
    if (m) p.set("month", m);
    if (n) p.set("travelers", n);
    nav(`/group-tours?${p.toString()}`);
  };
  return (
    <form onSubmit={go} className="rise-3 mt-10 grid w-full max-w-4xl gap-3 rounded-2xl bg-white/95 p-3 shadow-2xl backdrop-blur md:grid-cols-[1.4fr_1fr_0.8fr_auto]" data-testid="hero-search-form">
      <label className="rounded-xl px-4 py-2 text-left hover:bg-slate-50"><span className="label !mb-0">Destination</span>
        <select value={d} onChange={(e) => setD(e.target.value)} className="w-full bg-transparent text-sm font-semibold text-[#171717] outline-none" data-testid="hero-search-destination">
          <option value="">All destinations</option>{dests.map((x) => <option key={x}>{x}</option>)}
        </select></label>
      <label className="rounded-xl px-4 py-2 text-left hover:bg-slate-50"><span className="label !mb-0">Travel month</span>
        <select value={m} onChange={(e) => setM(e.target.value)} className="w-full bg-transparent text-sm font-semibold text-[#171717] outline-none" data-testid="hero-search-month">
          <option value="">Any month</option>{MONTHS.map((x) => <option key={x}>{x}</option>)}
        </select></label>
      <label className="rounded-xl px-4 py-2 text-left hover:bg-slate-50"><span className="label !mb-0">Travelers</span>
        <input type="number" min="1" max="50" value={n} onChange={(e) => setN(e.target.value)} className="w-full bg-transparent text-sm font-semibold text-[#171717] outline-none" data-testid="hero-search-travelers" /></label>
      <button className="btn-brand !rounded-xl" data-testid="hero-search-submit-btn"><Search className="h-4 w-4" />Search Trips</button>
    </form>
  );
};

const Hero = ({ tours }) => (
  <section className="relative flex min-h-[88vh] items-center overflow-hidden bg-slate-900 text-white" data-testid="home-hero">
    <img src="/images/hero.jpg" alt="Snow-capped Himalayan peaks above a mountain valley" className="absolute inset-0 h-full w-full object-cover" fetchpriority="high" />
    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-900/35 to-slate-950/30" />
    <div className="container-x relative py-24">
      <p className="eyebrow rise !text-white/90">Wandervibe Journeys · Uttarakhand</p>
      <h1 className="rise mt-4 max-w-3xl text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">Your Next Story <span className="text-[#FD691B]">Begins Here.</span></h1>
      <p className="rise-2 mt-6 max-w-xl text-base text-white/85 md:text-lg">Discover extraordinary places, meet incredible people, and experience journeys that stay with you long after you return.</p>
      <div className="rise-2 mt-8 flex flex-wrap gap-3">
        <Link to="/group-tours" className="btn-brand" data-testid="hero-explore-group-tours-btn">Explore Group Tours</Link>
        <Link to="/contact-us" className="btn-ghost-light" data-testid="hero-plan-trip-btn">Plan Your Trip</Link>
      </div>
      <HeroSearch tours={tours} />
    </div>
  </section>
);

const ImageFeature = ({ t, tall }) => (
  <Link to={tourPath(t)} className={`group relative block overflow-hidden rounded-3xl ${tall ? "min-h-[460px]" : "min-h-[300px]"}`} data-testid={`feature-${t.slug}`}>
    <img src={imgUrl(t.cover_image)} alt={t.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
    <div className="absolute bottom-0 p-7 text-white">
      <p className="text-xs font-semibold uppercase tracking-widest text-[#FD691B]">{durationLabel(t)}</p>
      <h3 className="mt-2 font-display text-2xl font-extrabold sm:text-3xl">{t.title}</h3>
      <p className="mt-2 max-w-md text-sm text-white/85">{t.short_description}</p>
      <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold">View itinerary <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
    </div>
  </Link>
);

const WHY = [
  { Icon: Route, t: "Carefully planned itineraries", d: "Routes paced around the mountains, the weather and the people travelling." },
  { Icon: Handshake, t: "Trusted local travel partners", d: "We work with people who know these valleys and roads." },
  { Icon: Users, t: "Group travel experiences", d: "Travel with like-minded people and leave with new friends." },
  { Icon: ListChecks, t: "Transparent trip inclusions", d: "Clear lists of what is and isn't included — no surprises." },
  { Icon: HeadphonesIcon, t: "Personalized travel assistance", d: "Talk to a real person before, during and after planning." },
  { Icon: Sparkles, t: "Memorable shared journeys", d: "Moments, conversations and stories worth remembering." },
];

const FamilySection = ({ family }) => (
  <section className="bg-white py-20 md:py-28" data-testid="home-family-section">
    <div className="container-x">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionTitle eyebrow="Family Tours" title="Comfortable, well-planned family holidays" sub="Easy pacing, comfortable stays and room to customise — for every generation of your family." />
        <Link to="/family-tours" className="btn-outline shrink-0" data-testid="home-explore-family-tours-btn">Explore Family Tours</Link>
      </div>
      {family?.length ? (
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">{family.slice(0, 3).map((t) => <TourCard key={t.slug} tour={t} family />)}</div>
      ) : (
        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {FAMILY_DESTINATIONS.map((d) => (
            <Link key={d.name} to={`/contact-us?category=Family%20Tour&destination=${encodeURIComponent(d.name)}`} className="group relative aspect-[3/4] overflow-hidden rounded-2xl" data-testid={`home-family-dest-${d.name.toLowerCase().replace(/\s/g, "-")}`}>
              <img src={d.image} alt={`Family holiday in ${d.name}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
              <div className="absolute bottom-0 p-4 text-white"><h3 className="font-display text-base font-bold">{d.name}</h3><p className="text-xs text-white/80">Plan a family trip</p></div>
            </Link>
          ))}
        </div>
      )}
    </div>
  </section>
);

export default function Home() {
  useSeo({ title: "Wandervibe Journeys | Himalayan Group Tours, Yatras & Family Holidays", description: "Group road trips to Spiti and Ladakh, Kedarnath, Do Dham and Char Dham yatras, and family holidays — planned by Wandervibe Journeys, Uttarakhand.", path: "/" });
  const { data: tours } = useFetch("/tours?type=group");
  const { data: family } = useFetch("/tours?type=family");
  const settings = useSettings();
  const by = (s) => tours?.find((t) => t.slug === s);
  const himalaya = ["spiti-valley", "ladakh"].map(by).filter(Boolean);
  const spiritual = ["kedarnath-yatra", "do-dham-yatra", "char-dham-yatra"].map(by).filter(Boolean);

  return (
    <>
      <Hero tours={tours} />
      <section className="py-20 md:py-28" data-testid="home-group-tours-section">
        <div className="container-x">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <SectionTitle eyebrow="Group Tours" title="Explore Group Tours" sub="Himalayan road trips and spiritual journeys, shared with fellow travellers." />
            <Link to="/group-tours" className="btn-outline shrink-0" data-testid="home-view-all-group-tours-btn">View All Group Tours</Link>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {(tours || []).filter((t) => t.featured).slice(0, 5).map((t) => <TourCard key={t.slug} tour={t} />)}
          </div>
        </div>
      </section>
      <FamilySection family={family} />
      {himalaya.length > 0 && (
        <section className="py-20 md:py-28" data-testid="home-himalayas-section">
          <div className="container-x">
            <SectionTitle eyebrow="Explore the Himalayas" title="High roads, wide skies and mountain villages" sub="Cold deserts, centuries-old monasteries and the kind of road trips you talk about for years." />
            <div className="mt-12 grid gap-6 lg:grid-cols-2">{himalaya.map((t) => <ImageFeature key={t.slug} t={t} tall />)}</div>
          </div>
        </section>
      )}
      {spiritual.length > 0 && (
        <section className="bg-white py-20 md:py-28" data-testid="home-spiritual-section">
          <div className="container-x">
            <SectionTitle eyebrow="Spiritual Journeys" title="Sacred yatras of the Garhwal Himalaya" sub="Road-based pilgrimages to Kedarnath, Badrinath, Gangotri and Yamunotri." />
            <div className="mt-12 grid gap-6 md:grid-cols-3">{spiritual.map((t) => <ImageFeature key={t.slug} t={t} />)}</div>
          </div>
        </section>
      )}
      <section className="py-20 md:py-28" data-testid="home-why-section">
        <div className="container-x">
          <SectionTitle eyebrow="Why Wandervibe" title="Why Choose Wandervibe?" />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {WHY.map(({ Icon, t, d }) => (
              <div key={t} className="rounded-2xl border border-slate-200 bg-white p-7">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#FFF3EC] text-[#FD691B]"><Icon className="h-6 w-6" /></span>
                <h3 className="mt-5 font-display text-lg font-bold">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-[#FD691B] text-white" data-testid="home-community-section">
        <div className="container-x grid items-center gap-12 py-20 md:py-28 lg:grid-cols-2">
          <div>
            <p className="eyebrow !text-white/80">The Wandervibe Community</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl">Travel Together. Feel More.</h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-white/90 md:text-lg">Some journeys are about the places you visit. Others are about the people you meet along the way. At Wandervibe, we bring people together through shared experiences, unforgettable landscapes, and stories worth remembering.</p>
            <Link to="/group-tours" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-display text-sm font-semibold text-[#FD691B] transition-transform hover:-translate-y-0.5" data-testid="home-community-cta">Find your group trip <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="/images/friends.jpg" alt="Friends on a mountain road trip" loading="lazy" className="aspect-[3/4] w-full rounded-2xl object-cover" />
            <img src="/images/roadtrip.jpg" alt="Travellers posing on a Himalayan road" loading="lazy" className="mt-10 aspect-[3/4] w-full rounded-2xl object-cover" />
          </div>
        </div>
      </section>
      <Testimonials />
      <section className="py-20 md:py-28" data-testid="home-instagram-section">
        <div className="container-x">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <SectionTitle eyebrow="@wandervibejourneys" title="Moments from the mountains" />
            {settings?.instagram_url && <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="btn-outline shrink-0" data-testid="home-instagram-link"><Instagram className="h-4 w-4" />Follow on Instagram</a>}
          </div>
          <div className="mt-12 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            {(settings?.gallery || []).slice(0, 8).map((src, i) => (
              <div key={src + i} className="aspect-square overflow-hidden rounded-2xl"><img src={imgUrl(src)} alt="Himalayan travel moment" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" /></div>
            ))}
          </div>
        </div>
      </section>
      <section className="relative overflow-hidden bg-[#171717] text-white" data-testid="home-final-cta">
        <img src="/images/snowroad.jpg" alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="container-x relative py-24 text-center md:py-32">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl">Where Are We Going Next?</h2>
          <p className="mt-4 text-base text-white/80 md:text-lg">Your next adventure is waiting.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/group-tours" className="btn-brand" data-testid="final-cta-explore-btn">Explore Trips</Link>
            <Link to="/contact-us" className="btn-ghost-light" data-testid="final-cta-expert-btn">Talk to Our Travel Expert</Link>
          </div>
        </div>
      </section>
    </>
  );
}
